import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputGroup,
  InputLeftElement,
  Flex,
  Text,
  TableContainer,
  VStack,
  Select,
  Button,
  HStack,
  useToast,
  Spinner,
  Badge,
  useBreakpointValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  FormControl,
  FormLabel,
  SimpleGrid,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
} from "@chakra-ui/icons";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Navbar from "../../Components/Navbar/Navbar";
import Sidebar from "../../Components/Sidebar";
import Pagination from "../../Components/Common/Pagination";
import { api_url } from "../../url";

const CreditHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [action, setAction] = useState("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // User Search State
  const [userSearch, setUserSearch] = useState("");
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedUsername, setSelectedUsername] = useState("");
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const toast = useToast();
  const SIDEBAR_WIDTH = "240px";
  const isMobile = useBreakpointValue({ base: true, md: false });

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${api_url}/transactions/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit,
          search,
          type,
          status,
          startDate,
          endDate,
          userId: selectedUserId,
          action: action, // Although action filter is not directly in the API getAllTransactions but I check if I should add it
        },
      });
      if (response.data.success) {
        // If action filter is active, we filter in frontend if backend doesn't support it directly
        // Backend getAllTransactions has no action filter yet.
        let txns = response.data.transactions;
        if (action) {
          txns = txns.filter(t => t.action === action);
        }
        setTransactions(txns);
        setTotalPages(response.data.pagination.pages);
        setTotalTransactions(response.data.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching credit history:", error);
      toast({
        title: "Error fetching credit history.",
        description: error.response?.data?.message || "Something went wrong.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSuggestedUsers([]);
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${api_url}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search: query, limit: 5 },
      });
      if (response.data.success) {
        setSuggestedUsers(response.data.users);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTransactions();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [page, search, type, status, startDate, endDate, selectedUserId, action]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchUsers(userSearch);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [userSearch]);

  const handleUserSelect = (u) => {
    setSelectedUserId(u._id);
    setSelectedUsername(u.username);
    setUserSearch(u.username);
    setSuggestedUsers([]);
    setIsUserMenuOpen(false);
    setPage(1);
  };

  const clearUserSelection = () => {
    setSelectedUserId("");
    setSelectedUsername("");
    setUserSearch("");
    setSuggestedUsers([]);
    setPage(1);
  };

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success": return "green";
      case "pending": return "orange";
      case "failed": return "red";
      case "refunded": return "blue";
      case "cancelled": return "gray";
      default: return "gray";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "deposit": return "blue";
      case "withdraw": return "orange";
      case "bet": return "red";
      case "win": return "green";
      case "bonus": return "purple";
      case "refund": return "cyan";
      case "transfer": return "teal";
      default: return "gray";
    }
  };

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box
        p={{ base: 4, md: 8 }}
        bg="gray.50"
        minH="100vh"
        mt="64px"
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
      >
        <Flex
          mb={6}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap={4}
        >
          <Text fontSize="2xl" fontWeight="bold" color="gray.800">
            Credit History
          </Text>
        </Flex>

        {/* Filters */}
        <Box bg="white" p={6} borderRadius="lg" mb={8} boxShadow="sm" border="1px solid" borderColor="gray.100">
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {/* User Search Select */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.600">User Filter</FormLabel>
              <Box position="relative">
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search User..."
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      setIsUserMenuOpen(true);
                      if (selectedUserId && e.target.value !== selectedUsername) {
                        setSelectedUserId("");
                        setSelectedUsername("");
                      }
                    }}
                    onFocus={() => setIsUserMenuOpen(true)}
                  />
                  {selectedUserId && (
                    <Button
                      size="xs"
                      position="absolute"
                      right="8px"
                      top="8px"
                      zIndex={2}
                      onClick={clearUserSelection}
                    >
                      Clear
                    </Button>
                  )}
                </InputGroup>
                {isUserMenuOpen && suggestedUsers.length > 0 && (
                  <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    bg="white"
                    boxShadow="lg"
                    borderRadius="md"
                    zIndex={10}
                    mt={1}
                    border="1px solid"
                    borderColor="gray.200"
                    maxH="200px"
                    overflowY="auto"
                  >
                    {suggestedUsers.map((u) => (
                      <Box
                        key={u._id}
                        p={3}
                        _hover={{ bg: "gray.50", cursor: "pointer" }}
                        onClick={() => handleUserSelect(u)}
                        borderBottom="1px solid"
                        borderColor="gray.100"
                      >
                        <Text fontWeight="600" fontSize="sm">{u.username}</Text>
                        <Text fontSize="xs" color="gray.500">{u.mobile} | {u.role}</Text>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </FormControl>

            {/* Date Filter */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.600">Start Date</FormLabel>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.600">End Date</FormLabel>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </FormControl>

            {/* Transaction Type Filter */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.600">Transaction Type</FormLabel>
              <Select
                placeholder="All Types"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="deposit">Deposit</option>
                <option value="withdraw">Withdraw</option>
                <option value="bet">Bet</option>
                <option value="win">Win</option>
                <option value="refund">Refund</option>
                <option value="transfer">Transfer</option>
                <option value="bonus">Bonus</option>
              </Select>
            </FormControl>

            {/* Action Filter */}
            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.600">Action</FormLabel>
              <Select
                placeholder="All Actions"
                value={action}
                onChange={(e) => setAction(e.target.value)}
              >
                <option value="credit">Credit (+)</option>
                <option value="debit">Debit (-)</option>
              </Select>
            </FormControl>
          </SimpleGrid>
          
          <Flex mt={6} justify="flex-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearch("");
                setType("");
                setAction("");
                setStatus("");
                setStartDate("");
                setEndDate("");
                setSelectedUserId("");
                setSelectedUsername("");
                setUserSearch("");
                setPage(1);
              }}
              mr={3}
            >
              Reset Filters
            </Button>
            <Button colorScheme="blue" size="sm" onClick={fetchTransactions}>Update View</Button>
          </Flex>
        </Box>

        {loading ? (
          <Flex justify="center" align="center" h="200px">
            <Spinner size="xl" color="blue.500" />
          </Flex>
        ) : (
          <>
            <Box
              bg="white"
              borderRadius="lg"
              overflow="hidden"
              boxShadow="sm"
              border="1px solid #e2e8f0"
            >
              <TableContainer>
                <Table variant="simple">
                  <Thead bg="gray.100">
                    <Tr>
                      <Th>TRX ID</Th>
                      <Th>User</Th>
                      <Th>Type</Th>
                      <Th>Action</Th>
                      <Th>Amount</Th>
                      <Th>Post Balance</Th>
                      <Th>Status</Th>
                      <Th>Description</Th>
                      <Th>Time</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {transactions.length > 0 ? (
                      transactions.map((txn) => (
                        <Tr key={txn._id} _hover={{ bg: "gray.50" }}>
                          <Td fontSize="xs" fontWeight="medium" title={txn.transactionId}>
                            {txn.transactionId?.substring(0, 12)}...
                          </Td>
                          <Td>
                             <Text fontSize="sm" fontWeight="bold">{txn.user?.username || "Unknown"}</Text>
                             <Text fontSize="xs" color="gray.500">{txn.user?.mobile}</Text>
                          </Td>
                          <Td>
                             <Badge colorScheme={getTypeColor(txn.actionType)} variant="subtle">
                               {txn.actionType}
                             </Badge>
                          </Td>
                          <Td>
                            <Badge
                              colorScheme={txn.action === "credit" ? "green" : "red"}
                              variant="solid"
                            >
                              {txn.action?.toUpperCase()}
                            </Badge>
                          </Td>
                          <Td
                            fontWeight="bold"
                            color={txn.action === "credit" ? "green.600" : "red.600"}
                          >
                            {txn.action === "credit" ? "+" : "-"}{txn.currency === "INR" ? "₹" : txn.currency}
                            {txn.amount?.toLocaleString()}
                          </Td>
                          <Td fontSize="sm" fontWeight="600">
                             ₹{txn.balanceAfter?.toLocaleString() || "0"}
                          </Td>
                          <Td>
                            <Badge colorScheme={getStatusColor(txn.status)}>
                              {txn.status}
                            </Badge>
                          </Td>
                          <Td
                            fontSize="xs"
                            maxW="200px"
                            isTruncated
                            title={txn.description}
                          >
                            {txn.description}
                          </Td>
                          <Td fontSize="xs" color="gray.600">
                            {new Date(txn.createdAt).toLocaleString()}
                          </Td>
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={9} textAlign="center" py={12} color="gray.500">
                          <VStack>
                            <Text>No transaction records found with current filters.</Text>
                            <Button size="sm" variant="link" onClick={() => {
                                 setStartDate("");
                                 setEndDate("");
                                 setSelectedUserId("");
                                 setPage(1);
                            }}>Clear Filters</Button>
                          </VStack>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            {/* Pagination */}
            <Pagination 
              currentPage={page}
              totalPages={totalPages}
              totalEntries={totalTransactions}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={handleLimitChange}
            />
          </>
        )}
      </Box>
    </>
  );
};

export default CreditHistory;
